// indent.ts - net-new (no cloud/playground precedent) CM6 `indentService` for
// dbt charts YAML, translating the intent of the vscode YAML
// `indentationRules.increaseIndentPattern`
// (apps/vscode-extension/language-configuration.json) into CM6's
// column-based indent API.
//
// CM6's `indentService` returns a target *column* for a line, not text to
// insert, so the vscode `onEnterRules` "appendText: '- '" behavior cannot be
// expressed through it. It lives here instead as a plain Enter command
// (`dbtChartsListContinuation`), which is the only place text - rather than a
// column - can be inserted.

import { type IndentContext, indentService } from "@codemirror/language";
import { EditorSelection, type Extension } from "@codemirror/state";
import { keymap, type Command, type EditorView } from "@codemirror/view";

// Mirrors vscode's `increaseIndentPattern`: a line ending (trailing
// whitespace aside) in a mapping colon, a lone sequence dash, or a `|`/`>`
// block-scalar header (with optional `-`/`+` chomping indicator) opens a
// nested indent level. A populated sequence item ("- foo") does not match -
// its trailing content isn't one of these markers - so it maintains the
// current level, matching vscode.
export const INCREASES_INDENT_RE = /(:|-|\|[-+]?|>[-+]?)\s*$/;

/**
 * Whether `text` opens a nested indent level. The one answer to that question,
 * shared with `format.ts` so the formatter and the indent service can never
 * disagree about the same line.
 *
 * The pattern anchors at end of line, so a trailing comment hides the marker
 * that precedes it (`revenue: # sql to a database` reads as ending in `e`).
 * Callers that can tell a real comment from a `#` inside a quoted value - the
 * formatter, which has the parse tree - pass the line already cut at the
 * comment. The indent service has no tree here and passes the line as typed:
 * it errs toward *not* opening a level, which costs one Enter's indentation
 * rather than restructuring a document.
 */
export function opensIndent(text: string): boolean {
  return INCREASES_INDENT_RE.test(text);
}

/** A populated sequence item: a dash with content after it on the same line. */
const POPULATED_ITEM_RE = /^([ \t]*)-([ \t]+)\S/;

/**
 * Net-new CM6 indent service for dbt charts YAML: after a line that opens a
 * nested level (mapping key, lone sequence dash, block-scalar header),
 * indent by one more unit than that line; otherwise maintain the previous
 * non-blank line's indent. Uses `context.unit` for the step size - never a
 * hardcoded column count - so it respects the editor's configured
 * `indentUnit`.
 */
export function dbtChartsIndentService(): Extension {
  return indentService.of((context: IndentContext, pos: number): number | null => {
    const doc = context.state.doc;
    let lineNumber = doc.lineAt(pos).number - 1;
    while (lineNumber >= 1) {
      const prev = doc.line(lineNumber);
      if (prev.text.trim() !== "") {
        const prevIndent = context.lineIndent(prev.from);
        return INCREASES_INDENT_RE.test(prev.text) ? prevIndent + context.unit : prevIndent;
      }
      lineNumber -= 1;
    }
    return 0;
  });
}

/**
 * Enter on a populated sequence item continues the sequence: newline, that
 * item's own indent, and a fresh `- `. Every item after the first otherwise
 * gets its dash typed by hand, because the indent service can only return a
 * column and the dash is text.
 *
 * Only a populated item that is *finished* continues. A bare `- ` opener means
 * the author is about to write the item's first key, and an item that opens a
 * block (`- cols:`, `- text: |`) has its own body coming next - in both cases
 * the indent service already puts the cursor where that content belongs, and a
 * dash there would splice a sibling item in front of the body. Anything with a
 * selection to replace, or more than one cursor, falls through to the ordinary
 * newline command - a continuation only makes sense for a single collapsed
 * cursor at the end of the item.
 */
export const dbtChartsListContinuation: Command = (view: EditorView): boolean => {
  const { state } = view;
  // CM6 runs keymap handlers in a read-only view (that is how search keys work
  // there), so a mutating command has to say no itself.
  if (state.readOnly) return false;
  const range = state.selection.main;
  if (state.selection.ranges.length !== 1 || !range.empty) return false;
  const line = state.doc.lineAt(range.head);
  if (range.head !== line.to) return false;
  if (INCREASES_INDENT_RE.test(line.text)) return false;
  const item = POPULATED_ITEM_RE.exec(line.text);
  if (!item) return false;
  // The new item copies the current one's own leading whitespace and dash
  // spacing verbatim: a sequence's items are siblings, so whatever column and
  // spacing this one uses is by definition what the next one needs.
  const insert = `\n${item[1]}-${item[2]}`;
  view.dispatch({
    changes: { from: range.head, insert },
    selection: EditorSelection.cursor(range.head + insert.length),
    userEvent: "input",
    scrollIntoView: true,
  });
  return true;
};

/**
 * `dbtChartsListContinuation` bound to Enter, at a precedence the caller keeps:
 * it must sit below the completion package's blank-line Enter (`Prec.highest`)
 * and above `defaultKeymap`'s `insertNewlineAndIndent`. Ships with the YAML
 * language, never in `dbtChartsKeymap()` - `- ` continuation in a SQL editor
 * would be nonsense.
 */
export function dbtChartsIndentKeymap(): Extension {
  return keymap.of([{ key: "Enter", run: dbtChartsListContinuation }]);
}
